import { useEffect, useState } from "react";
import { getFacilityKeyForPortal } from "@/store/profile";
import authRequest from "@/api/auth-request";
import type { PatientFacilityInfo } from "@/api/auth-request";
import { formatPhone } from "@/utils/format";
import { cn } from "@/utils/cn";

const WELCOME_SEEN_KEY = "portal-welcome-seen";

export function getWelcomeSeen(): boolean {
  try {
    return !!localStorage.getItem(WELCOME_SEEN_KEY);
  } catch {
    return false;
  }
}

export function setWelcomeSeen(): void {
  try {
    localStorage.setItem(WELCOME_SEEN_KEY, "1");
  } catch {
    // ignore
  }
}

export interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

export function WelcomeModal({ open, onClose }: WelcomeModalProps) {
  const [phone, setPhone] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const key = getFacilityKeyForPortal();
    if (!key) return;
    let cancelled = false;
    authRequest.request
      .getFacilityInfo(key)
      .then((res) => {
        if (!cancelled && res?.data?.phone != null)
          setPhone(String((res.data as PatientFacilityInfo).phone));
        else setPhone(null);
      })
      .catch(() => setPhone(null));
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setWelcomeSeen();
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  function handleClose() {
    setWelcomeSeen();
    onClose();
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-[max(1rem,env(safe-area-inset-top,0px))]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={cn(
          "relative flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-y-auto overscroll-contain rounded-2xl border border-border bg-white p-6 shadow-xl sm:p-10"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="welcome-modal-title"
          className="text-center text-2xl font-bold text-gray-900 sm:text-3xl"
        >
          Welcome
        </h2>
        <p className="mt-5 text-left text-base text-gray-800 leading-snug">
          Access health information online instead of over the phone or in person.
          The patient portal allows you to:
        </p>
        <ul className="mt-3 list-disc list-inside space-y-1.5 text-base text-gray-800 leading-snug">
          <li>Keep contact information current</li>
          <li>Request appointments</li>
          <li>Access documents</li>
          <li>And much more</li>
        </ul>
        <p className="mt-5 text-left text-base text-gray-800 leading-snug">
          If you have any questions or concerns, please feel free to contact us
          at {phone != null ? formatPhone(phone) : "…"}. We are here to help!
        </p>
        <div className="mt-6 flex justify-center sm:mt-8">
          <button
            type="button"
            onClick={handleClose}
            className="min-h-11 w-full rounded-lg bg-healthsource-red px-6 py-2.5 text-base font-bold text-white shadow transition-colors hover:bg-[var(--hs-red-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-healthsource-navy focus-visible:ring-offset-2 sm:w-auto"
          >
            Get started
          </button>
        </div>
      </div>
    </div>
  );
}
